import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

// Import refactored components
import { AvatarSection } from './Profile/AvatarSection';
import { CoverImageSection } from './Profile/CoverImageSection';
import { BasicInfoForm } from './Profile/BasicInfoForm';
import { PasswordChangeDialog } from './Profile/PasswordChangeDialog';
import { ThemeToggleSection } from './Profile/ThemeToggleSection';
import { PromotionalBannerSection } from './Profile/PromotionalBannerSection';

const formSchema = z.object({
  name: z.string().min(2, 'Nome muito curto'),
  email: z.string().email('Email inválido'),
  phone: z.string().optional(),
  bio: z.string().optional(),
  whatsapp: z.string().optional(),
  instagram: z.string().optional(),
  creci: z.string().optional(),
  slug: z.string().min(2, 'Link muito curto').max(50, 'Link muito longo')
    .regex(/^[a-z0-9-]+$/, 'Use apenas letras minúsculas, números e hífens'),
  primary_color: z.string(),
  primary_foreground: z.string(),
  accent_color: z.string(),
  accent_foreground: z.string(),
});

export function ProfileSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewCover, setPreviewCover] = useState<{ desktop: string | null; mobile: string | null }>({
    desktop: null,
    mobile: null
  });
  const [previewBanner, setPreviewBanner] = useState<{ desktop: string | null; mobile: string | null }>({
    desktop: null,
    mobile: null
  });
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const { user } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      bio: user?.bio || '',
      whatsapp: user?.whatsapp || '',
      instagram: user?.instagram || '',
      creci: user?.creci || '',
      slug: user?.slug || '',
      primary_color: user?.primary_color || '#0f172a',
      primary_foreground: user?.primary_foreground || '#f8fafc',
      accent_color: user?.accent_color || '#6366f1',
      accent_foreground: user?.accent_foreground || '#ffffff',
    },
  });

  // Helper function to extract only digits from phone number
  const extractDigits = (phoneNumber: string | null | undefined): string => {
    if (!phoneNumber) return '';
    return phoneNumber.replace(/\D/g, '');
  };

  useEffect(() => {
    if (user) {
      setPreviewImage(user.avatar_url || null);
      setPreviewCover({
        desktop: user.cover_url_desktop || null,
        mobile: user.cover_url_mobile || null
      });
      setPreviewBanner({
        desktop: user.promotional_banner_url_desktop || null,
        mobile: user.promotional_banner_url_mobile || null
      });
      setIsDarkTheme(user.theme === 'dark');
      
      // Reset form with processed WhatsApp value
      form.reset({
        name: user.name || '',
        email: user.email || '',
        phone: extractDigits(user.phone),
        bio: user.bio || '',
        whatsapp: extractDigits(user.whatsapp), // Extract only digits for WhatsApp
        instagram: user.instagram || '',
        creci: user.creci || '',
        slug: user.slug || '',
        primary_color: user.primary_color || '#0f172a',
        primary_foreground: user.primary_foreground || '#f8fafc',
        accent_color: user.accent_color || '#6366f1',
        accent_foreground: user.accent_foreground || '#ffffff',
      });
      
      setLoading(false);
    }
  }, [user, form]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    form.setValue('name', name);
    
    if (!form.getValues('slug')) {
      const slug = name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      form.setValue('slug', slug);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setSaving(true);

      // Format phone number
      let formattedPhone = null;
      if (values.phone) {
        const numbers = values.phone.replace(/\D/g, '');
        if (numbers.length === 11) {
          formattedPhone = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
        } else if (numbers.length === 10) {
          formattedPhone = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        }
      }

      // Format WhatsApp number
      let formattedWhatsApp = null;
      if (values.whatsapp) {
        const numbers = values.whatsapp.replace(/\D/g, '');
        if (numbers.length === 11) {
          formattedWhatsApp = `+55 ${numbers.slice(0, 2)} ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
        } else if (numbers.length === 10) {
          formattedWhatsApp = `+55 ${numbers.slice(0, 2)} ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        }
      }

      // Format Instagram handle
      let formattedInstagram = null;
      if (values.instagram) {
        formattedInstagram = values.instagram.replace(/^@/, '');
      }

      // Check if slug is unique
      if (values.slug !== user?.slug) {
        const { data: existingUser, error: checkError } = await supabase
          .from('users')
          .select('id')
          .eq('slug', values.slug)
          .maybeSingle();

        if (checkError) {
          throw checkError;
        }

        if (existingUser) {
          throw new Error('Este link já está sendo usado por outro corretor');
        }
      }

      const { error } = await supabase
        .from('users')
        .update({
          name: values.name,
          phone: formattedPhone,
          bio: values.bio,
          whatsapp: formattedWhatsApp,
          instagram: formattedInstagram,
          creci: user?.niche_type === 'imoveis' ? values.creci : null,
          slug: values.slug,
          primary_color: values.primary_color,
          primary_foreground: values.primary_foreground,
          accent_color: values.accent_color,
          accent_foreground: values.accent_foreground,
        })
        .eq('id', user?.id);

      if (error) throw error;

      toast.success('Perfil atualizado com sucesso');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {/* Avatar Section */}
          <AvatarSection 
            user={user}
            previewImage={previewImage}
            setPreviewImage={setPreviewImage}
          />

          <Separator className="my-6" />

          {/* Cover Images Section */}
          <CoverImageSection
            user={user}
            previewCover={previewCover}
            setPreviewCover={setPreviewCover}
          />

          <Separator className="my-6" />

          {/* Promotional Banner Section */}
          <PromotionalBannerSection
            user={user}
            previewBanner={previewBanner}
            setPreviewBanner={setPreviewBanner}
          />

          <Separator className="my-6" />

          {/* Basic Information */}
          <BasicInfoForm
            form={form}
            user={user}
            onNameChange={handleNameChange}
          />

          <FormField
            control={form.control}
            name="bio"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Biografia</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Separator className="my-6" />

          {/* Theme Toggle */}
          <ThemeToggleSection
            user={user}
            isDarkTheme={isDarkTheme}
            setIsDarkTheme={setIsDarkTheme}
          />

          <div className="flex justify-end space-x-4 mt-6">
            <PasswordChangeDialog
              user={user}
              open={showPasswordDialog}
              onOpenChange={setShowPasswordDialog}
            />
            
            <Button
              type="submit"
              disabled={saving}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar Alterações
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}